import { useEffect, useState, useRef, useCallback, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { KeyRound, ArrowRight, ShieldCheck, Compass, Gauge, Target } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { LangSwitcher } from '@/components/LangSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { loginEmail, resetPassword } from '@/lib/auth';
import { useT } from '@/lib/i18n';

/* ─── Constellation background (canvas-based particle network) ─── */
function ConstellationCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = (canvas.width = canvas.offsetWidth * devicePixelRatio);
    let h = (canvas.height = canvas.offsetHeight * devicePixelRatio);
    ctx.scale(devicePixelRatio, devicePixelRatio);

    const PARTICLE_COUNT = 80;
    const CONNECTION_DIST = 150;
    const particles: { x: number; y: number; vx: number; vy: number; r: number }[] = [];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * (w / devicePixelRatio),
        y: Math.random() * (h / devicePixelRatio),
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
      });
    }

    const draw = () => {
      const cw = w / devicePixelRatio;
      const ch = h / devicePixelRatio;
      ctx.clearRect(0, 0, cw, ch);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > cw) p.vx *= -1;
        if (p.y < 0 || p.y > ch) p.vy *= -1;
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.15;
            ctx.strokeStyle = `rgba(6, 182, 212, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(6, 182, 212, 0.6)';
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    const onResize = () => {
      w = canvas.width = canvas.offsetWidth * devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * devicePixelRatio;
      ctx.scale(devicePixelRatio, devicePixelRatio);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  useEffect(() => {
    const cleanup = init();
    return cleanup;
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full opacity-60"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}

export default function Login() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: { pathname?: string } } };
  const dest = loc.state?.from?.pathname ?? '/';

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy]         = useState(false);

  useEffect(() => {
    if (user) nav(dest, { replace: true });
  }, [user, dest, nav]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return toast.error(t('login.email_pwd_required'));
    setBusy(true);
    try {
      await loginEmail(email, password);
      await refresh();
    } catch (e) {
      toast.error(humanError((e as { code?: string; message: string }), t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#020a0e]">
      {/* Constellation animation */}
      <ConstellationCanvas />

      {/* Dramatic glow orbs */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500/[0.07] blur-[150px]" />
      <div className="pointer-events-none absolute -left-48 top-0 h-[500px] w-[500px] rounded-full bg-brand-600/[0.08] blur-[130px] animate-pulse" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[400px] w-[400px] rounded-full bg-brand-400/[0.06] blur-[120px] animate-pulse [animation-delay:2s]" />

      {/* Subtle scanline overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(6,182,212,0.4) 2px, rgba(6,182,212,0.4) 3px)',
          backgroundSize: '100% 4px',
        }}
      />

      {/* Header */}
      <header className="relative z-20 flex items-center justify-between px-6 py-5 sm:px-10">
        <Logo variant="lockup" tone="light" className="h-9 max-w-[200px] sm:h-10 sm:max-w-[240px]" />
        <LangSwitcher />
      </header>

      {/* Main content */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 pb-10 sm:px-6">
        <div className="grid w-full max-w-6xl items-center gap-16 lg:grid-cols-[1.1fr_1fr] lg:gap-24">

          {/* Brand panel */}
          <div className="hidden lg:block">
            <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-brand-400/30 bg-brand-500/10 px-3 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-brand-300">
              {t('login.brand.eyebrow')}
            </div>
            <h1 className="font-display text-5xl leading-[1.15] text-white xl:text-6xl">
              {t('login.brand.tagline_l1')}<br/>
              <span className="bg-gradient-to-r from-brand-400 to-brand-300 bg-clip-text text-transparent">
                {t('login.brand.tagline_l2')}
              </span>
            </h1>
            <div className="mt-8 flex flex-col gap-3.5">
              <Pillar icon={<Compass className="h-4 w-4" />} label={t('login.brand.pillar.audit')} />
              <Pillar icon={<Gauge className="h-4 w-4" />} label={t('login.brand.pillar.delivery')} />
              <Pillar icon={<Target className="h-4 w-4" />} label={t('login.brand.pillar.monitor')} />
            </div>

            <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-brand-400/20 bg-brand-500/5 px-4 py-2 text-xs text-brand-300/80">
              <ShieldCheck className="h-4 w-4" /> {t('login.brand.security')}
            </div>
          </div>

          {/* Login card with animated gradient border */}
          <div className="mx-auto w-full max-w-md">
            <div className="login-card-wrapper relative rounded-2xl p-[1px]">
              {/* Animated gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-[conic-gradient(from_var(--login-angle),transparent_30%,rgba(6,182,212,0.4)_50%,transparent_70%)] opacity-70 login-border-spin" />
              <div className="absolute inset-[1px] rounded-[15px] bg-[#0a1a1f]" />

              {/* Card content */}
              <div className="relative rounded-2xl bg-[#0a1a1f]/90 p-8 backdrop-blur-sm sm:p-10">
                {/* Subtle inner glow */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_1px_1px_rgba(6,182,212,0.1)]" />

                {/* Mobile logo */}
                <div className="mb-8 flex items-center justify-center lg:hidden">
                  <Logo variant="lockup" tone="light" className="h-12 max-w-[240px]" />
                </div>

                <h2 className="font-display text-2xl text-white">{t('login.title')}</h2>
                <p className="mt-1.5 text-sm text-ink-300/80">{t('login.subtitle')}</p>

                <form onSubmit={onSubmit} className="mt-8 space-y-5">
                  <div>
                    <Label className="text-ink-200/90">{t('common.email')}</Label>
                    <Input
                      type="email"
                      required
                      className="mt-1.5 border-brand-500/10 bg-[#061214] text-white placeholder:text-ink-500 focus:border-brand-400/40 focus:ring-brand-500/15 focus:shadow-[0_0_20px_rgba(6,182,212,0.08)]"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="vous@entreprise.com"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-ink-200/90">{t('common.password')}</Label>
                      <button
                        type="button"
                        className="text-xs font-medium text-brand-400/80 transition-colors hover:text-brand-300"
                        onClick={async () => {
                          if (!email) return toast.error(t('login.reset_email_first'));
                          try { await resetPassword(email); toast.success(t('login.reset_sent')); }
                          catch (e) { toast.error(humanError(e as { message: string }, t)); }
                        }}
                      >
                        {t('login.forgot')}
                      </button>
                    </div>
                    <Input
                      type="password"
                      required
                      className="mt-1.5 border-brand-500/10 bg-[#061214] text-white placeholder:text-ink-500 focus:border-brand-400/40 focus:ring-brand-500/15 focus:shadow-[0_0_20px_rgba(6,182,212,0.08)]"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full mt-2 shadow-[0_0_30px_rgba(6,182,212,0.2)] hover:shadow-[0_0_40px_rgba(6,182,212,0.3)]">
                    <KeyRound className="h-4 w-4" /> {t('login.cta')} <ArrowRight className="h-4 w-4" />
                  </Button>
                </form>

                <p className="mt-8 text-center text-xs text-ink-400/70">{t('login.no_account')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 px-6 pb-5 text-center text-xs text-ink-500 sm:px-10 sm:text-left">
        {t('login.copyright', { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
}

function Pillar({ icon, label }: { icon: React.ReactNode; label: string }) {
  if (!label) return null;
  return (
    <div className="group flex items-center gap-3 text-ink-200/80 transition-colors hover:text-white">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-500/10 text-brand-400 ring-1 ring-brand-400/20 transition-all group-hover:bg-brand-500/20 group-hover:ring-brand-400/40 group-hover:shadow-[0_0_12px_rgba(6,182,212,0.15)]">
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function humanError(e: { code?: string; message: string }, t: (k: 'auth.err.invalid' | 'auth.err.no_user' | 'auth.err.wrong_pwd' | 'auth.err.too_many' | 'auth.err.network' | 'auth.err.unknown') => string): string {
  switch (e.code) {
    case 'auth/invalid-credential':     return t('auth.err.invalid');
    case 'auth/user-not-found':         return t('auth.err.no_user');
    case 'auth/wrong-password':         return t('auth.err.wrong_pwd');
    case 'auth/too-many-requests':      return t('auth.err.too_many');
    case 'auth/network-request-failed': return t('auth.err.network');
    default: return e.message || t('auth.err.unknown');
  }
}
